/* iter183 — Dispatch SMS settings panel.
   Admin/office only. Lets Troy/dispatch:
   - Set the dispatch SMS number (default 614-285-4502).
   - Toggle notifications for en-route, completed, and cancelled events.
   All backed by existing endpoints:
     GET/PUT /api/admin/dispatch-sms-number
     GET/PUT /api/admin/sms/settings   (per-event toggles keyed by SMS_EVENT_KEYS)
*/
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Radio, Phone, Save } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// US phone: accept 10 digits, 11 digits starting with 1, or E.164 (+1XXX...).
const PHONE_RE = /^(\+?1?\s*[-.(]?\d{3}[-.\s)]?\s*\d{3}[-.\s]?\d{4})$/;

const DISPATCH_EVENTS = [
  {
    key: 'dispatch_tech_en_route',
    label: 'Notify dispatch when tech is en route',
    hint: 'Fires when a tech taps Start Job / Arrived. One SMS per appointment.',
  },
  {
    key: 'dispatch_tech_completed',
    label: 'Notify dispatch when tech completes job/shift',
    hint: 'Fires on Complete Job Time or Completion Wizard submit. Deduped.',
  },
  {
    key: 'dispatch_tech_cancelled',
    label: 'Notify dispatch when a shift is cancelled',
    hint: "Fires on the office 'Cancel Shift' action. Includes the reason if entered.",
  },
];

const DispatchSettingsTab = () => {
  const [number, setNumber] = useState('');
  const [savedNumber, setSavedNumber] = useState('');
  const [defaultNumber, setDefaultNumber] = useState('614-285-4502');
  const [events, setEvents] = useState({});
  const [eventsBaseline, setEventsBaseline] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingNumber, setSavingNumber] = useState(false);
  const [savingEvents, setSavingEvents] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/dispatch-sms-number`, { withCredentials: true }),
        axios.get(`${API}/admin/sms/settings`, { withCredentials: true }),
      ]);
      setNumber(nRes.data?.number || '');
      setSavedNumber(nRes.data?.number || '');
      setDefaultNumber(nRes.data?.default || '614-285-4502');
      const flat = (sRes.data?.events || {});
      const scoped = {};
      DISPATCH_EVENTS.forEach((e) => { scoped[e.key] = !!flat[e.key]; });
      setEvents(scoped);
      setEventsBaseline({ ...flat });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load dispatch settings');
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveNumber = async () => {
    const trimmed = number.trim();
    if (trimmed && !PHONE_RE.test(trimmed)) {
      toast.error('Please enter a valid US phone number, e.g. 614-285-4502');
      return;
    }
    setSavingNumber(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/dispatch-sms-number`,
        { number: trimmed },
        { withCredentials: true },
      );
      setSavedNumber(data.number || '');
      setNumber(data.number || '');
      toast.success(trimmed
        ? 'Dispatch number saved'
        : `Cleared — dispatch will use the default ${defaultNumber}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSavingNumber(false);
  };

  const saveEvents = async () => {
    setSavingEvents(true);
    try {
      // Merge new toggles into the full events payload so we don't wipe others.
      const merged = { ...eventsBaseline, ...events };
      const { data } = await axios.put(
        `${API}/admin/sms/settings`,
        { enabled: true, events: merged },
        { withCredentials: true },
      );
      setEventsBaseline({ ...(data?.events || merged) });
      toast.success('Dispatch notification toggles saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
    setSavingEvents(false);
  };

  if (loading) {
    return <p data-testid="dispatch-settings-loading" className="text-[#A3A3A3]">Loading…</p>;
  }

  const numberDirty = number.trim() !== savedNumber;
  const eventsDirty = DISPATCH_EVENTS.some((e) => Boolean(eventsBaseline[e.key]) !== Boolean(events[e.key]));

  return (
    <div data-testid="dispatch-settings-tab" className="space-y-6 max-w-3xl">
      <div>
        <h3 className="font-['Outfit'] text-xl font-semibold text-white flex items-center gap-2">
          <Radio className="w-5 h-5 text-[#D4AF37]" /> Dispatch SMS
        </h3>
        <p className="text-[#A3A3A3] text-sm mt-1">
          Internal dispatch/office texts when techs go en route, complete jobs, or a shift gets cancelled.
          This is separate from the customer-facing &quot;Technician En Route&quot; flow.
        </p>
      </div>

      {/* Dispatch number */}
      <div data-testid="dispatch-number-card" className="bg-[#141414] border border-[#262626] rounded-md p-4">
        <Label htmlFor="dispatch-sms-input" className="text-xs text-[#A3A3A3] uppercase tracking-wider flex items-center gap-1.5">
          <Phone className="w-3 h-3" /> Dispatch SMS Number
        </Label>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Input
            id="dispatch-sms-input"
            data-testid="dispatch-sms-input"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder={defaultNumber}
            className="bg-[#0A0A0A] border-[#262626] text-white max-w-xs font-mono"
          />
          <Button
            data-testid="dispatch-sms-save"
            onClick={saveNumber}
            disabled={savingNumber || !numberDirty}
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
          >
            <Save className="w-4 h-4 mr-1.5" /> {savingNumber ? 'Saving…' : 'Save'}
          </Button>
        </div>
        <p className="text-[#737373] text-[11px] mt-2 leading-relaxed">
          Leave blank to use the default <code className="text-[#D4AF37]">{defaultNumber}</code>.
          Format: <code>614-285-4502</code> or <code>+16142854502</code>. Only admin/office roles see this tab.
        </p>
      </div>

      {/* Event toggles */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-4 space-y-3">
        <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Dispatch notifications</p>
        {DISPATCH_EVENTS.map((e) => (
          <label
            key={e.key}
            data-testid={`dispatch-toggle-${e.key}`}
            className="flex items-start gap-3 bg-[#0A0A0A] border border-[#262626] rounded p-3 cursor-pointer"
          >
            <Switch
              checked={!!events[e.key]}
              onCheckedChange={(v) => setEvents((s) => ({ ...s, [e.key]: v }))}
              data-testid={`dispatch-switch-${e.key}`}
            />
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-bold">{e.label}</p>
              <p className="text-[#A3A3A3] text-[11px] mt-0.5">{e.hint}</p>
            </div>
          </label>
        ))}
        <div className="pt-1 flex items-center gap-2">
          <Button
            data-testid="dispatch-events-save"
            onClick={saveEvents}
            disabled={savingEvents || !eventsDirty}
            className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
          >
            <Save className="w-4 h-4 mr-1.5" /> {savingEvents ? 'Saving…' : 'Save Toggles'}
          </Button>
          {!eventsDirty && <p className="text-[#525252] text-xs">Nothing to save.</p>}
        </div>
      </div>
    </div>
  );
};

export default DispatchSettingsTab;
